import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, CheckCircle, XCircle, RefreshCw, Truck, Package, Activity, Search, Mail, Play, Clock, FileText, Upload } from 'lucide-react';
import { colors } from '../theme';

const GerenciamentoAPIs = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [statusCorreios, setStatusCorreios] = useState(null);
  const [statusFabril, setStatusFabril] = useState(null);
  const [statusGoogleVision, setStatusGoogleVision] = useState(null);
  const [statusBraspress, setStatusBraspress] = useState(null);
  const [statusRodonaves, setStatusRodonaves] = useState(null);
  const [processandoRodonaves, setProcessandoRodonaves] = useState(false);
  const [testando, setTestando] = useState({ correios: false, fabril: false, vision: false, braspress: false, rodonaves: false });
  const [statusESM, setStatusESM] = useState(null);
  const [processandoESM, setProcessandoESM] = useState(false);
  const [processandoRetro, setProcessandoRetro] = useState(false);
  const [resultadoESM, setResultadoESM] = useState(null);

  useEffect(() => {
    carregarStatus();
  }, []);

  const carregarStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('primaza_token');
      
      // Buscar status das APIs
      const [correiosRes, visionRes, brasspressRes, esmRes, rodonavesRes] = await Promise.allSettled([
        axios.get(`${API}/correios/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/google-vision/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/braspress/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/esm/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/rodonaves/status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (correiosRes.status === 'fulfilled') {
        setStatusCorreios(correiosRes.value.data);
      }
      
      if (visionRes.status === 'fulfilled') {
        setStatusGoogleVision(visionRes.value.data);
      }
      
      if (brasspressRes.status === 'fulfilled') {
        setStatusBraspress(brasspressRes.value.data);
      }

      if (esmRes.status === 'fulfilled') {
        setStatusESM(esmRes.value.data);
      }

      if (rodonavesRes.status === 'fulfilled') {
        setStatusRodonaves(rodonavesRes.value.data);
      }

      // Status Fabril (verificar última sincronização)
      setStatusFabril({ status: 'ativo', ultima_sync: 'Em produção' });

    } catch (error) {
      console.error('Erro ao carregar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const testarConexaoCorreios = async () => {
    setTestando({ ...testando, correios: true });
    try {
      const token = localStorage.getItem('primaza_token');
      await axios.get(`${API}/correios/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Conexão com API Correios OK!');
      await carregarStatus();
    } catch (error) {
      alert('❌ Erro na conexão: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestando({ ...testando, correios: false });
    }
  };

  const consultarESMAgora = async () => {
    setProcessandoESM(true);
    setResultadoESM(null);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(`${API}/esm/consultar-agora`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      setResultadoESM(response.data);
      alert(`Consulta concluída!\n\nConsultados: ${response.data.total_consultados}\nAtualizados: ${response.data.atualizados}\nEntregues: ${response.data.entregues}\nErros: ${response.data.erros}`);
      const statusRes = await axios.get(`${API}/esm/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatusESM(statusRes.data);
    } catch (error) {
      alert('Erro na consulta: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessandoESM(false);
    }
  };

  const executarRetroativo = async () => {
    setProcessandoRetro(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(`${API}/esm/retroativo`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      alert(`Retroativo concluído!\n\nConsultados: ${response.data.total_consultados}\nAtualizados: ${response.data.atualizados}\nEntregues: ${response.data.entregues}\nErros: ${response.data.erros}`);
      const statusRes = await axios.get(`${API}/esm/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatusESM(statusRes.data);
    } catch (error) {
      alert('Erro: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessandoRetro(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
            🔌 Gerenciamento de Integrações
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Monitore APIs de rastreamento animal e sincronização de pedidos
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.primaza.primary }} />
          </div>
        ) : (
          <>
            {/* API Correios */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-yellow-100">
                    <Package size={24} className="text-yellow-600" />
                  </div>
                  <div>
                    <span className="text-xl">API Correios</span>
                    <p className="text-sm text-gray-500 font-normal mt-1">
                      Rastreamento automático de encomendas
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Status:</span>
                      {statusCorreios?.status === 'configurado' ? (
                        <Badge className="bg-green-100 text-green-800 border-green-300">
                          <CheckCircle size={14} className="mr-1" />
                          Ativa
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800 border-red-300">
                          <XCircle size={14} className="mr-1" />
                          Inativa
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Informações */}
                  {statusCorreios?.status === 'configurado' && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Cartão de Postagem:</span>
                        <span className="font-mono font-medium">{statusCorreios.cartao_postagem}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Autenticação:</span>
                        <span className="font-medium text-green-600">✓ Configurada</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Sincronização Automática:</span>
                        <span className="font-medium">A cada 30 minutos</span>
                      </div>
                    </div>
                  )}

                  {/* Ações */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={testarConexaoCorreios}
                      disabled={testando.correios}
                      size="sm"
                    >
                      {testando.correios ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Activity size={16} className="mr-2" />
                      )}
                      Testar Conexão
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* API Fabril */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: colors.primaza.primary + '20' }}>
                    <Truck size={24} style={{ color: colors.primaza.primary }} />
                  </div>
                  <div>
                    <span className="text-xl">API Fabril (Primaza)</span>
                    <p className="text-sm text-gray-500 font-normal mt-1">
                      Sincronização de pedidos externos
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Status:</span>
                      <Badge className="bg-green-100 text-green-800 border-green-300">
                        <CheckCircle size={14} className="mr-1" />
                        Ativa (Produção)
                      </Badge>
                    </div>
                  </div>

                  {/* Informações */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Sincronização Automática:</span>
                      <span className="font-medium">A cada 10 minutos</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Ambiente:</span>
                      <span className="font-medium text-green-600">✓ Produção</span>
                    </div>
                  </div>

                  {/* Nota */}
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-sm text-blue-800">
                      <strong>ℹ️ Nota:</strong> A API Fabril está ativa apenas no ambiente de produção. 
                      No preview, use o botão &quot;Importar Fabril&quot; para testar.
                    </AlertDescription>
                  </Alert>

                  {/* Ações */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={async () => {
                        if (!window.confirm('Atualizar transportadoras e valores vazios? (NÃO sobrescreve dados existentes)')) return;
                        
                        try {
                          const token = localStorage.getItem('primaza_token');
                          const resp = await axios.post(`${API}/atualizar-transportadoras-vazias`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          
                          const msg = `✅ ${resp.data.message}\n\n` +
                            `📊 Detalhes:\n` +
                            `• Total sem transportadora: ${resp.data.total_sem_transportadora}\n` +
                            `• Pedidos atualizados: ${resp.data.atualizados}\n` +
                            `• Pedidos na API Fabril: ${resp.data.total_pedidos_na_api}\n` +
                            `• Com valorTotalNfe na API: ${resp.data.valores_nf_encontrados_na_api}\n\n` +
                            `${resp.data.valores_nf_encontrados_na_api === 0 ? '⚠️ A API Fabril não está retornando valores de NFE!' : '✅ Valores NFE foram atualizados!'}`;
                          
                          alert(msg);
                        } catch (error) {
                          alert('❌ Erro: ' + (error.response?.data?.detail || error.message));
                        }
                      }}
                      size="sm"
                      style={{ borderColor: colors.primaza.primary, color: colors.primaza.primary }}
                    >
                      <RefreshCw size={16} className="mr-2" />
                      Atualizar Dados Vazios
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* API Google Vision */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Search size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xl">🔍 Google Cloud Vision API</span>
                    <p className="text-sm text-gray-500 font-normal mt-1">
                      OCR e extração de texto de imagens
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Status:</span>
                      {statusGoogleVision ? (
                        <Badge className="bg-green-100 text-green-800 border-green-300">
                          <CheckCircle size={14} className="mr-1" />
                          Ativa
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600 border-gray-300">
                          Carregando...
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Informações de Uso */}
                  {statusGoogleVision && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Uso este mês ({statusGoogleVision.mes_referencia}):</span>
                        <span className="font-bold">{statusGoogleVision.total_chamadas_mes} / {statusGoogleVision.free_tier_limite}</span>
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${statusGoogleVision.percentual_usado > 80 ? 'bg-red-500' : statusGoogleVision.percentual_usado > 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(statusGoogleVision.percentual_usado, 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 text-center">
                        {statusGoogleVision.percentual_usado.toFixed(1)}% do free tier usado
                      </p>
                      
                      <div className="flex justify-between text-sm pt-2 border-t">
                        <span className="text-gray-600">Custo Estimado:</span>
                        <span className={`font-bold ${statusGoogleVision.dentro_free_tier ? 'text-green-600' : 'text-red-600'}`}>
                          {statusGoogleVision.dentro_free_tier ? 'R$ 0,00 (grátis)' : `R$ ${statusGoogleVision.custo_estimado_brl.toFixed(2)}`}
                        </span>
                      </div>
                      
                      {statusGoogleVision.ultima_chamada && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Última chamada:</span>
                          <span className="font-medium">{new Date(statusGoogleVision.ultima_chamada).toLocaleString('pt-BR')}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Nota */}
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-sm text-blue-800">
                      <strong>ℹ️ Free Tier:</strong> 1.000 imagens/mês grátis. 
                      Após isso: US$ 1,50 por 1.000 imagens (~R$ 7,50).
                    </AlertDescription>
                  </Alert>

                  {/* Histórico 6 meses */}
                  {statusGoogleVision?.historico_6_meses && (
                    <div className="mt-4">
                      <h4 className="font-semibold text-sm mb-3 text-gray-700">📊 Histórico (6 meses)</h4>
                      <div className="space-y-2">
                        {statusGoogleVision.historico_6_meses.map((mes, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <span className="text-xs text-gray-600 w-16">{mes.mes_nome}</span>
                            <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                              <div 
                                className={`h-4 rounded-full ${mes.total_chamadas > 800 ? 'bg-red-500' : mes.total_chamadas > 500 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                style={{ width: `${Math.min((mes.total_chamadas / 1000) * 100, 100)}%` }}
                              >
                                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                                  {mes.total_chamadas}
                                </span>
                              </div>
                            </div>
                            <span className="text-xs font-semibold w-20 text-right">
                              {mes.dentro_free_tier ? 'Grátis' : `R$ ${mes.custo_estimado_brl.toFixed(2)}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* API Braspress */}
            <Card className="border-2 border-orange-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100">
                    <Truck size={24} className="text-orange-600" />
                  </div>
                  <div>
                    <span className="text-xl">API Braspress</span>
                    <p className="text-sm text-gray-500 font-normal mt-1">
                      Rastreamento + Cotacao automatico (API v3)
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Status:</span>
                      {statusBraspress?.configurado ? (
                        <Badge className="bg-green-100 text-green-800 border-green-300">
                          <CheckCircle size={14} className="mr-1" />
                          Configurada
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                          <XCircle size={14} className="mr-1" />
                          Nao Configurada
                        </Badge>
                      )}
                    </div>
                    {statusBraspress?.configurado && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                          disabled={testando.braspress}
                          onClick={async () => {
                            setTestando(prev => ({...prev, braspress: true}));
                            try {
                              const token = localStorage.getItem('primaza_token');
                              const res = await axios.post(`${API}/braspress/consultar-agora?limit=10`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              alert(`Braspress: ${res.data.total_consultados} consultados, ${res.data.atualizados} atualizados, ${res.data.entregues} entregues, ${res.data.erros} erros`);
                            } catch (err) {
                              alert('Erro: ' + (err.response?.data?.detail || err.message));
                            } finally {
                              setTestando(prev => ({...prev, braspress: false}));
                              loadData();
                            }
                          }}
                        >
                          {testando.braspress ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw size={14} />}
                          <span className="ml-1">Consultar Agora</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          disabled={testando.braspress}
                          onClick={async () => {
                            setTestando(prev => ({...prev, braspress: true}));
                            try {
                              const token = localStorage.getItem('primaza_token');
                              const res = await axios.post(`${API}/braspress/retroativo?limit=50`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              alert(`Retroativo Braspress: ${res.data.total_consultados} consultados, ${res.data.atualizados} atualizados, ${res.data.erros} erros`);
                            } catch (err) {
                              alert('Erro: ' + (err.response?.data?.detail || err.message));
                            } finally {
                              setTestando(prev => ({...prev, braspress: false}));
                              loadData();
                            }
                          }}
                        >
                          Retroativo
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Informações detalhadas */}
                  {statusBraspress?.configurado ? (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Pedidos Braspress:</span>
                        <span className="font-bold">{statusBraspress.total_pedidos || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Nao Entregues:</span>
                        <span className="font-bold text-orange-600">{statusBraspress.nao_entregues || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Com Tracking API:</span>
                        <span className="font-bold text-blue-600">{statusBraspress.com_tracking_api || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">URL API:</span>
                        <span className="font-medium text-xs">{statusBraspress.url_api}</span>
                      </div>
                    </div>
                  ) : (
                    <Alert className="bg-yellow-50 border-yellow-200">
                      <AlertDescription className="text-sm text-yellow-800">
                        <strong>Configuracao Pendente:</strong> Configure CNPJ, usuario e senha no .env para ativar.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Funcionalidades */}
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-sm text-blue-800">
                      <strong>Funcionalidades:</strong> Rastreamento por NF (v3 com timeline), cotacao de frete, notificacao por email, atualizacao automatica a cada 15 min. Preenche CIF/FOB automaticamente.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>


            {/* Card: API Tracking Expresso São Miguel */}
            <Card className="border-l-4 border-l-teal-500">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-teal-600" />
                    <span>API Tracking - Expresso São Miguel</span>
                  </div>
                  <Badge data-testid="esm-status-badge" variant={statusESM?.configurado ? 'default' : 'destructive'}
                    style={statusESM?.configurado ? { backgroundColor: '#14b8a6' } : {}}>
                    {statusESM?.configurado ? 'Ativo' : 'Não Configurado'}
                  </Badge>
                </CardTitle>
                {statusESM?.tracking_provider && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">Provider:</span>
                    <Badge variant="outline" className="text-xs">API</Badge>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {statusESM ? (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-teal-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-teal-600 font-medium">Total Pedidos</p>
                        <p className="text-xl font-bold text-teal-700">{statusESM.total_pedidos}</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-orange-600 font-medium">Não Entregues</p>
                        <p className="text-xl font-bold text-orange-700">{statusESM.nao_entregues}</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-blue-600 font-medium">Com Tracking API</p>
                        <p className="text-xl font-bold text-blue-700">{statusESM.com_tracking_api}</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      Consulta automática a cada 15 minutos
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        data-testid="esm-consultar-btn"
                        onClick={consultarESMAgora}
                        disabled={processandoESM || processandoRetro}
                        size="sm"
                        style={{ backgroundColor: '#14b8a6' }}
                        className="text-white"
                      >
                        {processandoESM ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Play size={16} className="mr-2" />
                        )}
                        {processandoESM ? 'Consultando...' : 'Consultar Agora'}
                      </Button>
                      <Button
                        data-testid="esm-retroativo-btn"
                        onClick={executarRetroativo}
                        disabled={processandoESM || processandoRetro}
                        size="sm"
                        variant="outline"
                        className="border-teal-500 text-teal-700"
                      >
                        {processandoRetro ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw size={16} className="mr-2" />
                        )}
                        {processandoRetro ? 'Preenchendo...' : 'Preencher Retroativo'}
                      </Button>
                    </div>
                    {resultadoESM && (
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm space-y-1">
                        <p className="font-medium text-teal-800">Resultado da consulta:</p>
                        <p>Consultados: {resultadoESM.total_consultados} | Atualizados: {resultadoESM.atualizados}</p>
                        <p>Entregues: {resultadoESM.entregues || 0} | Comprovantes: {resultadoESM.comprovantes} | Erros: {resultadoESM.erros}</p>
                        {resultadoESM.ignorados_parados > 0 && (
                          <p className="text-gray-500">Ignorados (sem mudança): {resultadoESM.ignorados_parados}</p>
                        )}
                        {resultadoESM.detalhes && resultadoESM.detalhes.length > 0 && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-teal-700 font-medium">Ver detalhes ({resultadoESM.detalhes.length})</summary>
                            <div className="mt-1 max-h-40 overflow-y-auto text-xs">
                              {resultadoESM.detalhes.map((d, i) => (
                                <div key={i} className="py-1 border-b border-teal-100">
                                  Pedido {d.pedido} (NF {d.nfe}): {d.erro || `${d.status || 'ok'} - ${d.ocorrencias || 0} ocorrência(s)${d.cte ? ` | CT-e: ${d.cte}` : ''}${d.frete ? ` | Frete: R$${d.frete}` : ''}${d.comprovante ? ` | POD: ${d.comprovante}` : ''}`}
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4 text-gray-400">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Carregando...
                  </div>
                )}
              </CardContent>
            </Card>

            {/* API Rodonaves */}
            <Card className="border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Truck size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xl">API Rodonaves</span>
                    <p className="text-sm text-gray-500 font-normal mt-1">
                      Rastreamento automático via API REST
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Status:</span>
                      {statusRodonaves?.configurado ? (
                        <Badge className="bg-green-100 text-green-800 border-green-300">
                          <CheckCircle size={14} className="mr-1" />
                          Configurada
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                          <XCircle size={14} className="mr-1" />
                          Nao Configurada
                        </Badge>
                      )}
                    </div>
                    {statusRodonaves?.configurado && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          disabled={testando.rodonaves}
                          data-testid="rodonaves-consultar-btn"
                          onClick={async () => {
                            setTestando(prev => ({...prev, rodonaves: true}));
                            try {
                              const token = localStorage.getItem('primaza_token');
                              const res = await axios.post(`${API}/rodonaves/consultar-agora`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              alert(`Rodonaves: ${res.data.total_consultados} consultados, ${res.data.atualizados} atualizados, ${res.data.entregues} entregues, ${res.data.erros} erros`);
                              const statusRes = await axios.get(`${API}/rodonaves/status`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setStatusRodonaves(statusRes.data);
                            } catch (err) {
                              alert('Erro: ' + (err.response?.data?.detail || err.message));
                            } finally {
                              setTestando(prev => ({...prev, rodonaves: false}));
                            }
                          }}
                        >
                          {testando.rodonaves ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw size={14} />}
                          <span className="ml-1">Consultar Agora</span>
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Informações detalhadas */}
                  {statusRodonaves?.configurado ? (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Pedidos Rodonaves:</span>
                        <span className="font-bold">{statusRodonaves.total_pedidos || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Nao Entregues:</span>
                        <span className="font-bold text-blue-600">{statusRodonaves.nao_entregues || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Com Tracking API:</span>
                        <span className="font-bold text-green-600">{statusRodonaves.com_tracking_api || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">URL API:</span>
                        <span className="font-medium text-xs">{statusRodonaves.url_api}</span>
                      </div>
                    </div>
                  ) : (
                    <Alert className="bg-yellow-50 border-yellow-200">
                      <AlertDescription className="text-sm text-yellow-800">
                        <strong>Configuracao Pendente:</strong> Configure RODONAVES_USERNAME e RODONAVES_PASSWORD no .env para ativar.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Funcionalidades */}
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-sm text-blue-800">
                      <strong>Funcionalidades:</strong> Rastreamento por NF com timeline de eventos, atualizacao automatica a cada 15 min. CTeNumber preenchido automaticamente.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>

            {/* Futuras Integrações */}
            <Card className="border-dashed border-2">
              <CardContent className="p-6">
                <div className="text-center py-8">
                  <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">➕</span>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">Adicionar Nova Integração</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Preparado para integrar com outras transportadoras e serviços
                  </p>
                  <p className="text-xs text-gray-400">
                    Em breve: Jadlog, Total Express, Loggi, e mais...
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default GerenciamentoAPIs;
